import React, { useEffect } from "react";
import { useController } from "react-hook-form";
import PropsProvider from "../PropsProvider";

import { useTranslation } from "react-i18next";

const DefaultRenderer = ({ checked, onChange, option, labelName, valueName, validate }) => {
	return (
		<div className="checkbox">
			<label className={`checkbox-custom ${validate ? "has-error" : ""}`}>
				<input
					checked={checked}
					onChange={onChange}
					type="checkbox"
					value={option[valueName]}
				/>
				<span className="checkmark" />
				<span className="checkbox-label">{option[labelName]}</span>
			</label>
		</div>
	);
};

/**
 * @param { Props } props
 *
 * @typedef Props
 * @property {object} control - useForm control
 * @property {string} name - field name
 * @property {string} labelName - custom label property name
 * @property {string} valueName - custom value property name
 * @property {object} validation - validating object
 * @property {{ label: string, value: any}[]} options
 *
 */
const Checkbox = (props) => {
	const {
		control,
		name,
		validation,
		options,
		children,
		labelName = "label",
		valueName = "value",
		required,
		validate,
		isAllChecked = null,
		setAllSelected,
		setAllCheck,
	} = props;

	const { t } = useTranslation();

	const { field } = useController({
		control,
		name,
		rules: {
			required:
				required === true ? t("common.components.Checkbox.requiredInput") : required === null ? " " : required,
			...validation,
		},
	});

	const value = field.value || [];

	useEffect(() => {
		const allValues = options.map((option) => option[valueName]?.toString());
		field.onChange(allValues);
	}, []);

	useEffect(() => {
		if (isAllChecked === true) {
			const allValues = options.map((option) => option[valueName]?.toString());
			field.onChange(allValues);
		} else if (isAllChecked === false) {
			field.onChange([]);
		}
	}, [isAllChecked]);

	const onChange = (e) => {
		if (setAllCheck) {
			setAllCheck(false);
		}

		if (e.target.value === "ALL") {
			setAllSelected(!isAllChecked);
			if (isAllChecked === true) {
				const allValues = options.map((option) => option[valueName]?.toString());
				field.onChange(allValues);
			} else if (isAllChecked === false) {
				field.onChange([]);
			}
		} else {
			if (value.find((v) => v === "ALL")) {
				const tmpValue = value.filter((v) => v !== "ALL" && v !== e.target.value);
				field.onChange([...tmpValue]);
				setAllSelected(true);
			} else if (
				value.length === options.length - 1 &&
				!value.find((v) => v === e.target.value)
			) {
				setAllSelected(true);
				const allValues = options.map((option) => option[valueName]?.toString());
				field.onChange(allValues);
			} else {
				value.find((v) => v === e.target.value)
					? field.onChange(value.filter((v) => v !== e.target.value))
					: field.onChange([...value, e.target.value]);
			}
		}
	};

	return options.map((option, index) => {
		const checked = value.includes(option[valueName]?.toString());
		return children ? (
			<PropsProvider key={index} props={{ checked, onChange, option, labelName, valueName }}>
				{children}
			</PropsProvider>
		) : (
			<DefaultRenderer
				key={index}
				{...{ checked, onChange, option, labelName, valueName, validate }}
			/>
		);
	});
};

export default Checkbox;
